/**
 * Content generation service API calls — thin wrappers around the HTTP client.
 *
 * Generation jobs are async: POST /v1/generate returns 202 with a job_id;
 * callers must poll GET /v1/generate/{job_id} until status is
 * 'completed' | 'failed'.
 */

import { contentGenApi } from './client';
import type {
  ApiResponse,
  PromptLibraryOut,
  PromptLibraryList,
  PromptOut,
  PromptList,
  PromptVariableDef,
  GenerationApproach,
  GenerateJobCreated,
  GenerateJobOut,
  PaperIndexOut,
  UserSettingsOut,
} from '../types';

function unwrap<T>(response: ApiResponse<T>): T {
  if (!response.data) {
    throw new Error(response.error?.message ?? 'Empty response from server');
  }
  return response.data;
}

// ---------------------------------------------------------------------------
// Prompt Libraries
// ---------------------------------------------------------------------------

export async function listLibraries(
  limit = 50,
  offset = 0,
  search?: string,
): Promise<PromptLibraryList> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.set('search', search);
  const response = await contentGenApi.get<ApiResponse<PromptLibraryList>>(
    `/v1/prompt-libraries?${params}`,
  );
  return unwrap(response);
}

export async function createLibrary(body: {
  name: string;
  description?: string | null;
}): Promise<PromptLibraryOut> {
  const response = await contentGenApi.post<ApiResponse<PromptLibraryOut>>(
    '/v1/prompt-libraries',
    body,
  );
  return unwrap(response);
}

export async function deleteLibrary(id: string): Promise<void> {
  return contentGenApi.delete(`/v1/prompt-libraries/${id}`);
}

// ---------------------------------------------------------------------------
// Prompts
// ---------------------------------------------------------------------------

export async function listPrompts(
  libraryId: string,
  limit = 100,
  offset = 0,
): Promise<PromptList> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const response = await contentGenApi.get<ApiResponse<PromptList>>(
    `/v1/prompt-libraries/${libraryId}/prompts?${params}`,
  );
  return unwrap(response);
}

export async function createPrompt(
  libraryId: string,
  body: {
    title: string;
    template: string;
    description?: string | null;
    section_match?: string | null;
    section_match_type?: 'exact' | 'regex' | 'any' | null;
    variables?: PromptVariableDef[];
    system_prompt?: string | null;
    model?: string | null;
    max_tokens?: number | null;
    position?: number;
    approach?: GenerationApproach;
  },
): Promise<PromptOut> {
  const response = await contentGenApi.post<ApiResponse<PromptOut>>(
    `/v1/prompt-libraries/${libraryId}/prompts`,
    body,
  );
  return unwrap(response);
}

export async function updatePrompt(
  libraryId: string,
  promptId: string,
  body: {
    title?: string;
    template?: string;
    description?: string | null;
    section_match?: string | null;
    section_match_type?: 'exact' | 'regex' | 'any' | null;
    variables?: PromptVariableDef[];
    system_prompt?: string | null;
    model?: string | null;
    max_tokens?: number | null;
    position?: number;
    approach?: GenerationApproach;
  },
): Promise<PromptOut> {
  const response = await contentGenApi.put<ApiResponse<PromptOut>>(
    `/v1/prompt-libraries/${libraryId}/prompts/${promptId}`,
    body,
  );
  return unwrap(response);
}

export async function deletePrompt(
  libraryId: string,
  promptId: string,
): Promise<void> {
  return contentGenApi.delete(
    `/v1/prompt-libraries/${libraryId}/prompts/${promptId}`,
  );
}

// ---------------------------------------------------------------------------
// Generation
// ---------------------------------------------------------------------------

export interface GenerateBody {
  prompt_id?: string | null;
  prompt_text?: string | null;
  variables?: Record<string, string>;
  approach?: GenerationApproach;
  index_id?: string | null;
  options?: {
    model?: string;
    max_tokens?: number;
  };
}

export async function submitGeneration(
  body: GenerateBody,
): Promise<GenerateJobCreated> {
  const response = await contentGenApi.post<ApiResponse<GenerateJobCreated>>(
    '/v1/generate',
    body,
  );
  return unwrap(response);
}

export async function getGeneration(jobId: string): Promise<GenerateJobOut> {
  const response = await contentGenApi.get<ApiResponse<GenerateJobOut>>(
    `/v1/generate/${jobId}`,
  );
  return unwrap(response);
}

// ---------------------------------------------------------------------------
// Polling helper
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2_000;
const DEFAULT_POLL_TIMEOUT_MS = 120_000; // 2 min
const ARIES_POLL_TIMEOUT_MS = 600_000; // 10 min

/** Poll until status is 'completed' or 'failed'; throws on timeout or failure. */
export async function pollGeneration(
  jobId: string,
  onPoll?: (data: GenerateJobOut) => void,
  timeoutMs = DEFAULT_POLL_TIMEOUT_MS,
): Promise<GenerateJobOut> {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const data = await getGeneration(jobId);
    onPoll?.(data);

    if (data.status === 'completed') return data;
    if (data.status === 'failed') {
      throw new Error(data.error ?? 'Generation failed without error detail');
    }
    if (data.status === 'cancelled') {
      throw new Error('Generation was cancelled');
    }

    await new Promise<void>((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  throw new Error(`Generation timed out after ${timeoutMs / 1000}s`);
}

export { ARIES_POLL_TIMEOUT_MS, DEFAULT_POLL_TIMEOUT_MS };

// ---------------------------------------------------------------------------
// Paper indexes & settings
// ---------------------------------------------------------------------------

export async function listPaperIndexes(): Promise<PaperIndexOut[]> {
  const response = await contentGenApi.get<ApiResponse<PaperIndexOut[]>>(
    '/v1/paper-indexes',
  );
  return unwrap(response);
}

export async function getUserSettings(): Promise<UserSettingsOut> {
  const response = await contentGenApi.get<ApiResponse<UserSettingsOut>>(
    '/v1/settings',
  );
  return unwrap(response);
}

export async function updateUserSettings(
  body: UserSettingsOut,
): Promise<UserSettingsOut> {
  const response = await contentGenApi.put<ApiResponse<UserSettingsOut>>(
    '/v1/settings',
    body,
  );
  return unwrap(response);
}

export const PAPERQA_POLL_TIMEOUT_MS = 600_000;
